import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Upload,
  X,
  Plus,
  ImageIcon,
  BookOpen,
  Tag,
  Loader2,
  PenTool,
  DollarSign,
  Coffee,
  Move,
  Trash2,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '../utils/cloudinaryUpload';

// Pre-defined categories and tags
const PREDEFINED_CATEGORIES = [
  'Action', 'Comedy', 'Drama', 'Romance', 'Dark Fantasy',
  'Psych Thriller', 'Sci-Fi', 'Horror', 'Slice of Life',
  'Mystery', 'Adventure', 'Superhero', 'AI Comics', 'Manga',
  'Webtoon', 'Historical', 'Sports', 'Music', 'Fantasy'
];

const PREDEFINED_TAGS = [
  'Ongoing', 'Completed', 'Short Story', 'One-Shot', 'Full Color',
  'Black & White', 'Mature', 'All Ages', 'AI Generated',
  'Hand Drawn', 'Digital Art', 'Indie', 'Debut', 'Fan Art',
  'Original', 'Adaptation', 'Collaboration', 'Featured Artist'
];

const CreateComicStreamlined = () => {
  const [comicData, setComicData] = useState({
    title: '',
    description: '',
    categories: [],
    tags: [],
    tips_enabled: true  // Enable tips by default
  });
  const [episodes, setEpisodes] = useState([
    {
      id: 1,
      title: 'Episode 1',
      slides: []
    }
  ]);
  const [currentEpisode, setCurrentEpisode] = useState(0);
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const { user, token, API } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleComicDataChange = (field, value) => {
    setComicData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleCategory = (category) => {
    setComicData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const addCategory = () => {
    if (newCategory.trim() && !comicData.categories.includes(newCategory.trim())) {
      setComicData(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory.trim()]
      }));
      setNewCategory('');
    }
  };

  const removeCategory = (category) => {
    setComicData(prev => ({
      ...prev,
      categories: prev.categories.filter(cat => cat !== category)
    }));
  };

  const toggleTag = (tag) => {
    setComicData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !comicData.tags.includes(newTag.trim())) {
      setComicData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setComicData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // Episode Management
  const addNewEpisode = () => {
    const newEpisodeNumber = episodes.length + 1;
    setEpisodes(prev => [
      ...prev,
      {
        id: newEpisodeNumber,
        title: `Episode ${newEpisodeNumber}`,
        slides: []
      }
    ]);
  };

  const updateEpisodeTitle = (episodeIndex, title) => {
    setEpisodes(prev => prev.map((ep, idx) =>
      idx === episodeIndex ? { ...ep, title } : ep
    ));
  };

  const deleteEpisode = (episodeIndex) => {
    if (episodes.length > 1) {
      setEpisodes(prev => prev.filter((_, idx) => idx !== episodeIndex));
      if (currentEpisode >= episodeIndex && currentEpisode > 0) {
        setCurrentEpisode(prev => prev - 1);
      }
    }
  };

  // Slide Management - Now using Cloudinary
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    let uploaded = 0;
    const totalFiles = files.length;

    for (const file of files) {
      try {
        // Upload to Cloudinary
        const result = await uploadImage(file, token, 'comic-panels', (progress) => {
          setUploadProgress(Math.round((uploaded / totalFiles) * 100 + (progress / totalFiles)));
        });

        if (result.success) {
          const newSlide = {
            id: Date.now() + Math.random(),
            image: result.url,  // Cloudinary URL
            public_id: result.public_id,
            filename: file.name,
            width: result.width,
            height: result.height
          };

          setEpisodes(prev => prev.map((ep, idx) =>
            idx === currentEpisode
              ? { ...ep, slides: [...ep.slides, newSlide] }
              : ep
          ));

          uploaded++;
          setUploadProgress(Math.round((uploaded / totalFiles) * 100));
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    setUploadProgress(0);

    if (uploaded > 0) {
      toast.success(`Uploaded ${uploaded} image${uploaded > 1 ? 's' : ''} successfully!`);
    }
  };

  const removeSlide = (slideId) => {
    setEpisodes(prev => prev.map((ep, idx) =>
      idx === currentEpisode
        ? { ...ep, slides: ep.slides.filter(slide => slide.id !== slideId) }
        : ep
    ));
  };

  const moveSlide = (slideIndex, direction) => {
    const episode = episodes[currentEpisode];
    if (!episode) return;

    const newIndex = direction === 'up' ? slideIndex - 1 : slideIndex + 1;
    if (newIndex < 0 || newIndex >= episode.slides.length) return;

    const newSlides = [...episode.slides];
    [newSlides[slideIndex], newSlides[newIndex]] = [newSlides[newIndex], newSlides[slideIndex]];

    setEpisodes(prev => prev.map((ep, idx) =>
      idx === currentEpisode ? { ...ep, slides: newSlides } : ep
    ));
  };

  const validateForm = () => {
    if (!comicData.title.trim()) {
      setError('Comic title is required');
      return false;
    }

    if (episodes.length === 0) {
      setError('At least one episode is required');
      return false;
    }

    const hasSlides = episodes.some(ep => ep.slides.length > 0);
    if (!hasSlides) {
      setError('At least one episode must have slides');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create comic with episodes
      const comicPayload = {
        title: comicData.title,
        description: comicData.description,
        categories: comicData.categories,
        tags: comicData.tags,
        tips_enabled: comicData.tips_enabled,
        episodes: episodes.map(ep => ({
          title: ep.title,
          slides: ep.slides.map(slide => slide.image)
        }))
      };

      const response = await axios.post(`${API}/comics/streamlined`, comicPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Comic created successfully!');
      navigate(`/streamlined-comic/${response.data.id}`);

    } catch (error) {
      console.error('Error creating comic:', error);
      setError(error.response?.data?.detail || 'Failed to create comic');
      toast.error('Failed to create comic');
    } finally {
      setLoading(false);
    }
  };

  const currentEpisodeData = episodes[currentEpisode];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
            <PenTool className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-fredoka">Create New Comic</h1>
          <p className="text-gray-400 mt-2">Create your comic with episodes and manage slides easily</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Comic Details */}
          <Card className="bg-gray-800/80 backdrop-blur border-purple-500/30">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-white">
                <BookOpen className="w-5 h-5 text-purple-400" />
                <span>Comic Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-gray-300">Comic Title *</Label>
                  <Input
                    id="title"
                    value={comicData.title}
                    onChange={(e) => handleComicDataChange('title', e.target.value)}
                    placeholder="Enter comic title"
                    required
                    className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-gray-300">Description</Label>
                <Textarea
                  id="description"
                  value={comicData.description}
                  onChange={(e) => handleComicDataChange('description', e.target.value)}
                  placeholder="Describe your comic story..."
                  rows={3}
                  className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                />
              </div>

              {/* Categories — Pre-defined picks + custom input */}
              <div className="space-y-3">
                <Label className="text-gray-300">Categories</Label>
                {/* Selected badges */}
                {comicData.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {comicData.categories.map(category => (
                      <Badge key={category} variant="secondary" className="bg-purple-600/30 text-purple-300 border-purple-500/50 flex items-center space-x-1">
                        <span>{category}</span>
                        <X className="w-3 h-3 cursor-pointer hover:text-red-400" onClick={() => removeCategory(category)} />
                      </Badge>
                    ))}
                  </div>
                )}
                {/* Pre-defined category grid */}
                <div className="flex flex-wrap gap-2">
                  {PREDEFINED_CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 border ${comicData.categories.includes(cat)
                          ? 'bg-purple-600 text-white border-purple-500 shadow-md shadow-purple-500/20'
                          : 'bg-gray-700/40 text-gray-400 border-gray-600/50 hover:border-purple-500/50 hover:text-purple-300'
                        }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                {/* Custom category input */}
                <div className="flex space-x-2">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Or type a custom category..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                    className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500 text-sm"
                  />
                  <Button type="button" onClick={addCategory} size="sm" className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tags — Pre-defined picks + custom input */}
              <div className="space-y-3">
                <Label className="text-gray-300">Tags</Label>
                {/* Selected badges */}
                {comicData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {comicData.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="border-cyan-500/50 text-cyan-300 flex items-center space-x-1">
                        <Tag className="w-3 h-3" />
                        <span>{tag}</span>
                        <X className="w-3 h-3 cursor-pointer hover:text-red-400" onClick={() => removeTag(tag)} />
                      </Badge>
                    ))}
                  </div>
                )}
                {/* Pre-defined tag grid */}
                <div className="flex flex-wrap gap-2">
                  {PREDEFINED_TAGS.map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTag(t)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 border ${comicData.tags.includes(t)
                          ? 'bg-cyan-600 text-white border-cyan-500 shadow-md shadow-cyan-500/20'
                          : 'bg-gray-700/40 text-gray-400 border-gray-600/50 hover:border-cyan-500/50 hover:text-cyan-300'
                        }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {/* Custom tag input */}
                <div className="flex space-x-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Or type a custom tag..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500 text-sm"
                  />
                  <Button type="button" onClick={addTag} size="sm" className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tips/Monetization Toggle - Temporarily Disabled */}
              <div className="space-y-2 pt-4 border-t border-gray-700 opacity-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Coffee className="w-5 h-5 text-gray-500" />
                    <Label className="text-gray-500">Enable Support/Tips</Label>
                  </div>
                  <div
                    className="relative w-12 h-6 rounded-full bg-gray-600 cursor-not-allowed"
                    title="Coming soon"
                  >
                    <span
                      className="absolute top-1 w-4 h-4 bg-gray-400 rounded-full translate-x-1"
                    />
                  </div>
                </div>
                <p className="text-gray-500 text-xs">
                  Coming soon - Tips feature is temporarily disabled
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Episodes Management */}
          <Card className="bg-gray-800/80 backdrop-blur border-purple-500/30">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-white">
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-purple-400" />
                  <span>Episodes</span>
                </div>
                <Button type="button" onClick={addNewEpisode} size="sm" className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Episode
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Episode Tabs */}
              <div className="flex space-x-2 overflow-x-auto">
                {episodes.map((episode, index) => (
                  <div key={episode.id} className="flex items-center space-x-2 min-w-max">
                    <Button
                      type="button"
                      variant={currentEpisode === index ? "default" : "outline"}
                      onClick={() => setCurrentEpisode(index)}
                      size="sm"
                      className={currentEpisode === index
                        ? "bg-purple-600 hover:bg-purple-700 text-white"
                        : "border-gray-600 text-gray-300 hover:bg-gray-700"}
                    >
                      Episode {index + 1}
                    </Button>
                    {episodes.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteEpisode(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Current Episode Details */}
              {currentEpisodeData && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Episode Title</Label>
                    <Input
                      value={currentEpisodeData.title}
                      onChange={(e) => updateEpisodeTitle(currentEpisode, e.target.value)}
                      placeholder="Enter episode title"
                      className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                    />
                  </div>

                  {/* Slide Management */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-300">Slides ({currentEpisodeData.slides.length})</Label>
                      <div>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id={`episode-${currentEpisode}-upload`}
                          disabled={uploading}
                        />
                        <Button
                          type="button"
                          onClick={() => document.getElementById(`episode-${currentEpisode}-upload`).click()}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Uploading {uploadProgress}%
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Add Slides
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Upload Progress Bar */}
                    {uploading && (
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}

                    {/* Slides Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {currentEpisodeData.slides.map((slide, slideIndex) => (
                        <Card key={slide.id} className="relative group bg-gray-700/50 border-gray-600">
                          <CardContent className="p-3">
                            <div className="aspect-square relative overflow-hidden rounded-lg">
                              <img
                                src={slide.image}
                                alt={`Slide ${slideIndex + 1}`}
                                className="w-full h-full object-cover"
                              />

                              {/* Slide Controls */}
                              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => moveSlide(slideIndex, 'up')}
                                  disabled={slideIndex === 0}
                                  className="text-white hover:bg-white hover:bg-opacity-20"
                                >
                                  <Move className="w-4 h-4 rotate-180" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => moveSlide(slideIndex, 'down')}
                                  disabled={slideIndex === currentEpisodeData.slides.length - 1}
                                  className="text-white hover:bg-white hover:bg-opacity-20"
                                >
                                  <Move className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeSlide(slide.id)}
                                  className="text-red-400 hover:bg-red-500 hover:bg-opacity-20"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-2 truncate">
                              Slide {slideIndex + 1}
                            </p>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Upload Placeholder */}
                      <Card
                        className="cursor-pointer border-dashed border-2 border-gray-600 hover:border-purple-400 transition-colors bg-gray-700/30"
                        onClick={() => document.getElementById(`episode-${currentEpisode}-upload`).click()}
                      >
                        <CardContent className="p-6 flex flex-col items-center justify-center aspect-square">
                          <Upload className="w-8 h-8 text-gray-500 mb-2" />
                          <p className="text-sm text-gray-500 text-center">
                            Click to add slides
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-3 text-white"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Comic...
                </>
              ) : (
                <>
                  <PenTool className="w-4 h-4 mr-2" />
                  Create Comic
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateComicStreamlined;